/**
 * UserDeviceService
 * Flutter parity:
 * - lib/services/app_authentication_service.dart:612-648 (reads `device_id` from SharedPreferences)
 * - lib/services/firebase_messaging_service.dart:98-123 (writes `device_id` to SharedPreferences)
 *
 * On web there is no native device identifier (no `identifierForVendor` or
 * Android `fingerprint`), so we synthesise a stable per-browser id with
 * `crypto.randomUUID()` and persist it via SecureStorageWebCrypto. The id is
 * generated lazily on first call and reused thereafter.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { firestoreService } from '@/data/services/firestoreService';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { UserDeviceRemapper } from '@/domain/remappers/userDevice/userDeviceRemapper';
import type { Unsubscribe } from 'firebase/firestore';

export class UserDeviceService {
  // De-dupes concurrent in-flight check-then-write attempts. Without this,
  // parallel callers (e.g. `SetUserDeviceUseCase` + `SubscribeToUserDeviceChangesUseCase`
  // fired in parallel from `UserDeviceListener` after a logout cleared
  // SecureStorage) both observe an empty slot and generate distinct UUIDs,
  // racing the persist. The losing UUID gets used as `localDeviceId` in the
  // subscription while the winner is written to Firestore, causing a self-
  // triggered mismatch on the next `modified` event and a spurious
  // `autoLoggedOut`. Cleared once resolved so subsequent calls re-read from
  // storage and stay correct if storage is wiped mid-session.
  private static inFlightLocalDeviceId: Promise<string> | null = null;

  /**
   * Reads the persisted local device id WITHOUT creating one.
   *
   * Used by the startup takeover check to distinguish "fresh sign-in"
   * (no local id yet — nothing to compare against) from "returning device"
   * (local id exists — a different remote id means another browser took
   * over while we were closed and we must sign out).
   *
   * Returns `null` when SecureStorage has no entry — i.e. first-ever visit,
   * post sign-out, or storage cleared.
   */
  static async getStoredLocalDeviceId(): Promise<string | null> {
    const storage = SecureStorageWebCrypto.getInstance();
    return storage.getEncrypted<string>(SECURE_STORAGE_KEYS.DEVICE_ID);
  }

  /**
   * One-shot read of the remote `user_device/{userId}` deviceId.
   *
   * Returns `null` when no document exists or the doc has no `deviceId`
   * field. Errors propagate to the caller — the takeover check fails open
   * (does not sign out) on Firestore errors.
   */
  static async getRemoteDeviceId(userId: string): Promise<string | null> {
    const data = await firestoreService.getUserDevice(userId);
    if (!data) return null;
    const entity = UserDeviceRemapper.toEntity(data);
    return entity.deviceId || null;
  }

  /**
   * Returns the device id for this browser, creating and persisting one on first call.
   */
  static getOrCreateLocalDeviceId(): Promise<string> {
    if (this.inFlightLocalDeviceId) return this.inFlightLocalDeviceId;
    this.inFlightLocalDeviceId = (async () => {
      try {
        const storage = SecureStorageWebCrypto.getInstance();
        const existing = await storage.getEncrypted<string>(SECURE_STORAGE_KEYS.DEVICE_ID);
        if (existing) return existing;

        const deviceId = crypto.randomUUID();
        await storage.setEncrypted(SECURE_STORAGE_KEYS.DEVICE_ID, deviceId);
        return deviceId;
      } finally {
        this.inFlightLocalDeviceId = null;
      }
    })();
    return this.inFlightLocalDeviceId;
  }

  /**
   * Writes `{userId, deviceId}` to Firestore `user_device/{userId}`.
   *
   * Flutter parity:
   * - lib/services/firebase_messaging_service.dart:98-123 (caller `setDeviceInfo`)
   * - lib/data/data_sources/user_device_data_source.dart:5-38 (writer)
   *
   * The web port reads the locally-persisted device id (creating one on first
   * call) and forwards the write to `firestoreService.setUserDevice`, which
   * performs a `setDoc(..., { merge: true })`. Errors are NOT caught here —
   * they propagate to the caller (the use case swallows + logs on startup so
   * a transient write failure does not block app initialization).
   */
  static async setDeviceInfo(userId: string): Promise<void> {
    const deviceId = await this.getOrCreateLocalDeviceId();
    await firestoreService.setUserDevice(userId, deviceId);
  }

  /**
   * Subscribes to `user_device/{userId}` changes and fires `onMismatch` when
   * a remote `deviceId` differs from the locally-persisted one.
   *
   * Flutter parity: lib/services/app_authentication_service.dart:612-648.
   *
   * Parity rules enforced here (mirroring the Dart stream listener):
   * - Only `modified` changes are inspected. `added` (the initial snapshot
   *   replays every doc as `added`) and `removed` are skipped — Flutter's
   *   listener guards with `change.type == DocumentChangeType.modified`.
   * - An empty/missing remote `deviceId` does not trigger `onMismatch`.
   * - On the FIRST mismatching `modified` change, `onMismatch` is invoked
   *   and iteration stops (Flutter's `break;` after `logOut()`).
   *
   * The local device id is resolved ONCE when the subscription is created
   * (via `getOrCreateLocalDeviceId`) and closed over in the callback. This
   * avoids a crypto-decrypt + IndexedDB hop on every Firestore snapshot tick.
   * The id is immutable for the lifetime of the subscription (created on
   * first sign-in and persists across the session), so caching is safe.
   *
   * The sign-out side-effect is the caller's responsibility — this method
   * only signals the mismatch, just as Flutter calls `logOut()` directly
   * from the stream listener (not from a deeper service).
   */
  static async subscribeToDeviceChanges(
    userId: string,
    onMismatch: () => void,
  ): Promise<Unsubscribe> {
    const localDeviceId = await this.getOrCreateLocalDeviceId();
    return firestoreService.subscribeToUserDeviceChanges(
      userId,
      changes => {
        for (const change of changes) {
          if (change.type !== 'modified') continue;
          const entity = UserDeviceRemapper.toEntity(change.data);
          if (entity.deviceId && entity.deviceId !== localDeviceId) {
            onMismatch();
            return;
          }
        }
      },
      error => appLogger.error('user_device subscription error', 'UserDeviceService.subscribeToDeviceChanges', { error: error }),
    );
  }
}
